import React, { createContext, useContext, useCallback } from 'react';
import useApiService from './ApiService';
import CONFIG from './CONFIG';
import { useAuth0 } from '@auth0/auth0-react';
import axios from 'axios';
import { useParams } from 'react-router-dom';

const ArtifactApiContext = createContext(null);

export function ArtifactApiProvider({ children }) {
  const { getAccessTokenSilently } = useAuth0();
  const { organizationId } = useParams();
  const { get, post, put, del } = useApiService(CONFIG.SERVER_ORGANIZATION);

  const getArtifact = useCallback(
    async (skip, limit, type) => {
      if (!skip) {
        skip = 0;
      }
      if (!limit) {
        limit = 10;
      }
      try {
        let endpoint = `/v1/artifacts?skip=${skip}&limit=${limit}`;
        if (type) {
          const types = Array.isArray(type) ? type : [type];
          endpoint += types.map((t) => `&type=${t}`).join('');
        }
        const response = await get(endpoint);
        return response.data;
      } catch (error) {
        console.error('Error fetching artifact:', error);
        throw error;
      }
    },
    [get],
  );

  const getArtifactById = useCallback(
    async (artifactId) => {
      try {
        const response = await get(`/v1/artifacts/${artifactId}`);
        return response.data;
      } catch (error) {
        console.error('Error fetching Artifact by ID:', error);
        throw error;
      }
    },
    [get],
  );

  const createArtifact = useCallback(
    async (artifact) => {
      try {
        const response = await post(`/v1/artifacts`, artifact);
        return response.data;
      } catch (error) {
        console.error('Error creating Artifact:', error);
        throw error;
      }
    },
    [post],
  );

  const updateArtifactById = useCallback(
    async (artifactId, artifact) => {
      try {
        const response = await put(`/v1/artifacts/${artifactId}`, artifact);
        return response.data;
      } catch (error) {
        console.error('Error updating Artifact:', error);
        throw error;
      }
    },
    [put],
  );

  const generateTestPDF = useCallback(
    async (artifactId, parameters) => {
      const fullUrl = `${CONFIG.SERVER_ORGANIZATION}/v1/artifacts/generate-test-pdf/${artifactId}`;

      try {
        const token = await getAccessTokenSilently();

        const response = await axios.post(fullUrl, parameters, {
          responseType: 'blob',
          headers: {
            Authorization: `Bearer ${token}`,
            'X-Organization-Key': organizationId,
          },
        });

        return URL.createObjectURL(response.data);
      } catch (error) {
        console.error('Error generate Test PDF:', error);
        return null;
      }
    },
    [getAccessTokenSilently, organizationId],
  );

  const deleteArtifact = useCallback(
    async (artifactId) => {
      try {
        const response = await del(`/v1/artifacts/${artifactId}`);
        return response.data;
      } catch (error) {
        console.error('Error deleting Artifact:', error);
        throw error;
      }
    },
    [del],
  );

  return (
    <ArtifactApiContext.Provider
      value={{
        getArtifact,
        getArtifactById,
        createArtifact,
        updateArtifactById,
        generateTestPDF,
        deleteArtifact,
      }}
    >
      {children}
    </ArtifactApiContext.Provider>
  );
}

export function useArtifactApi() {
  const context = useContext(ArtifactApiContext);
  if (!context) {
    throw new Error('useArtifactApi must be used within a ArtifactApiProvider');
  }
  return context;
}
